import { useState, useRef, useEffect } from 'react'
import { useDashboardStore } from '../../store/dashboardStore'
import { useT } from '../../hooks/useTranslation'
import { useChartData } from '../../hooks/useChartData'

// ── Month boundary X-axis labels ───────────────────────────────────────────
function getMonthLabels(data, monthNames) {
  const seen = new Set()
  return data.reduce((acc, d, i) => {
    const mo   = d.fullDate?.slice(5, 7) ?? ''
    const moN  = +mo
    if (!seen.has(mo) && moN >= 1 && moN <= 12) {
      seen.add(mo)
      acc.push({ i, text: monthNames[moN - 1] })
    }
    return acc
  }, [])
}

// ── Y-axis number formatter ────────────────────────────────────────────────
function fmtY(v) {
  const r = Math.round(v * 10) / 10
  return r % 1 === 0 ? r.toFixed(0) : r.toFixed(1)
}

// ── Equity point date ──────────────────────────────────────────────────────
// Точки кривой эквити (период «Всего») приходят с сервера как { fullDate:
// 'YYYY-MM-DD', value } — поля label у них нет, поэтому в подсказке была видна
// только сумма (жалоба владельца 2026-10-28: «на точках видно только профит,
// нужно чтобы была информация о времени, дате»). Приводим дату к виду ДД.ММ.ГГГГ.
function fmtEquityDate(fullDate) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(fullDate ?? ''))
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(fullDate ?? '')
}

// ── Shared tooltip row ─────────────────────────────────────────────────────
function TRow({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
      <span style={{ fontSize: 11, color: '#636366' }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color }}>{value}</span>
    </div>
  )
}

// ── Unified tooltip ────────────────────────────────────────────────────────
// detailed=true  → bar stats (Профит / Сделок / Объём / Win Rate / Ср. PnL)
// detailed=false → equity curve cumulative PnL
function ChartTooltip({ point, x, y, containerWidth, t, detailed }) {
  if (!point) return null
  const TW  = detailed ? 172 : 148
  const mg  = 8
  const cw  = containerWidth ?? 320
  const cx  = Math.max(mg + TW / 2, Math.min(cw - mg - TW / 2, x))
  const shift  = x - cx
  const pnlPos = point.pnl >= 0
  // Заголовок: у столбцов — готовый label (день/неделя/месяц), у точек кривой
  // эквити label нет — берём дату из fullDate (2026-10-28 → 28.10.2026).
  const header = detailed ? point.label : (point.label || fmtEquityDate(point.fullDate))

  return (
    <div style={{
      position: 'absolute', left: cx, top: y,
      transform: 'translate(-50%, calc(-100% - 12px))',
      zIndex: 200, background: '#1C1C1E',
      border: '1px solid rgba(255,107,53,0.28)',
      borderRadius: 12, padding: detailed ? '10px 14px' : '8px 12px',
      minWidth: TW, boxShadow: '0 8px 32px rgba(0,0,0,0.75)',
      pointerEvents: 'none',
    }}>
      {/* Arrow */}
      <div style={{
        position: 'absolute', bottom: -6,
        left: `calc(50% + ${shift}px)`,
        transform: 'translateX(-50%) rotate(45deg)',
        width: 10, height: 10, background: '#1C1C1E',
        border: '1px solid rgba(255,107,53,0.28)',
        borderLeft: 'none', borderTop: 'none',
      }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: detailed ? 6 : 4 }}>
        {/* Date — prominent orange header */}
        <span style={{
          fontSize: 14, fontWeight: 700, color: '#34D399', display: 'block',
          paddingBottom: 6, marginBottom: 2,
          borderBottom: '1px solid rgba(255,107,53,0.2)',
        }}>{header}</span>

        {detailed ? (
          <>
            <TRow label={t.chartProfit}  value={`${pnlPos ? '+' : '-'}$${Math.abs(point.pnl).toFixed(2)}`}                   color={pnlPos ? '#34D399' : '#FF3B30'} />
            <TRow label={t.chartTrades}  value={point.trades}                                                                   color="#fff" />
            <TRow label={t.chartVolume}  value={`$${Number(point.volume).toLocaleString()}`}                                   color="#fff" />
            <TRow label={t.chartWinRate} value={`${point.winRate}%`}                                                           color="#34D399" />
          </>
        ) : (
          // Simple: cumulative equity value — с подписью, чтобы было видно,
          // что это накопленный профит на эту дату, а не сумма за день.
          <>
            <TRow label={t.chartProfit}
              value={`${point.value >= 0 ? '+' : '-'}$${Math.abs(point.value).toFixed(2)}`}
              color={point.value >= 0 ? '#34D399' : '#FF3B30'} />
          </>
        )}
      </div>
    </div>
  )
}

// ── Bar Chart — День / Неделя / Месяц ─────────────────────────────────────
// Ось Y — фиксированная колонка слева (flex), график — скроллируемая область
// рядом. Данные НЕ перекрываются осью, поэтому левая часть не режется.
function BarChart({ data, containerRef, scrollRef, cardW, t, activeFullDate, onSetActive }) {
  const VH    = 170
  const padL  = 40, padR = 10, padT = 14, padB = 30
  const plotH = VH - padT - padB   // 126px

  const availW = Math.max(80, (cardW || 320) - 16 - padL)

  // ── Торговые периоды и раскладка ─────────────────────────────────────────
  // Ширина свечи — ИСХОДНАЯ (как в обычном графике): 60% ячейки, потолок 22px.
  // Свечи НЕ растягиваются на всю ширину графика.
  const cellW = availW / data.length
  const barW  = Math.max(3, Math.min(cellW * 0.6, 22))

  // Скругление углов свечи — под общий скруглённый стиль приложения
  // (минимальный радиус 2px, максимум 5px; на узких свечах — пропорционально).
  const rx = Math.min(Math.max(barW * 0.35, 2), 5)

  // Зазор между свечами — 2px (плотно, как в нормальном графике).
  const GAP_CON = 2

  // Позиция свечи = её номер в окне × (ширина свечи + зазор). Благодаря этому
  // ПРОПУЩЕННЫЕ дни/недели/месяцы занимают ровно столько места, сколько заняли
  // бы настоящие свечи: пропустил 2 дня → пустое место соответствует двум
  // свечкам, пропустил месяц → месту одной свечки. Подряд идущие периоды стоят
  // плотно (зазор всего 2px).
  const layout = []
  for (let i = 0; i < data.length; i++) {
    const d = data[i]
    if (!d.future && d.trades > 0) {
      layout.push({ d, x: i * (barW + GAP_CON) })
    }
  }
  const occupiedW = layout.length ? layout[layout.length - 1].x + barW : 0

  // Value range (real data only)
  const realVals  = layout.map(l => l.d.value)
  const maxPosRaw = Math.max(0, ...realVals)
  const maxNegRaw = Math.max(0, ...realVals.map(v => -v))
  const maxPos    = Math.ceil(maxPosRaw * 1.15 / 0.5) * 0.5 || 1
  const maxNeg    = maxNegRaw > 0.01 ? Math.ceil(maxNegRaw * 1.15 / 0.5) * 0.5 : 0
  const range     = maxPos + maxNeg

  // Y → pixel converters
  const zeroY  = padT + (maxPos / range) * plotH
  const barRect = v => v >= 0
    ? { y: zeroY - (v / range) * plotH, h: Math.max(2, (v / range) * plotH) }
    : { y: zeroY,                        h: Math.max(2, (-v / range) * plotH) }

  // Ширина SVG: минимум — во всю карточку; если свечей больше, чем влезает,
  // область расширяется и скроллится (свечи НЕ сжимаются).
  const VW = Math.max(availW, occupiedW)

  // Y-axis ticks: 3 ticks (include negative range if any)
  const yTicks = maxNeg > 0.01
    ? [-maxNeg, 0, maxPos]
    : [0, Number((maxPos / 2).toFixed(1)), maxPos]

  // Подписи месяцев — по ПЕРВОЙ свече каждого месяца из раскладки
  // (пустые периоды не считаются, зазоры не ломают подписи).
  const monthLabels = []
  const monthSeen = new Set()
  for (const { d, x } of layout) {
    const mo  = d.fullDate?.slice(5, 7) ?? ''
    const moN = +mo
    if (!monthSeen.has(mo) && moN >= 1 && moN <= 12) {
      monthSeen.add(mo)
      monthLabels.push({ x: x + barW / 2, text: t.monthNames[moN - 1] })
    }
  }

  // Anchor for tooltip — must be called synchronously before setState
  const getAnchor = e => {
    const con  = containerRef.current?.getBoundingClientRect()
    const rect = e?.currentTarget?.getBoundingClientRect()
    if (!con || !rect) return { x: 80, y: 20, containerWidth: 320 }
    return {
      x:              rect.left + rect.width / 2 - con.left,
      y:              rect.top  - con.top,
      containerWidth: con.width,
    }
  }

  // Build tooltip point from raw bar data
  const makePoint = d => ({
    label:    d.topLabel && d.bottomLabel
      ? `${d.topLabel} ${d.bottomLabel}`.trim()
      : (d.fullDate ?? ''),
    pnl:      d.value,
    trades:   d.trades,
    volume:   d.volume,
    winRate:  d.winRate,
    avgPnl:   d.avgPnl,
    fullDate: d.fullDate,
  })

  return (
    <div style={{ display: 'flex' }}>
      {/* Fixed Y-axis column */}
      <div style={{ width: padL, flexShrink: 0 }}>
        <svg width={padL} height={VH} style={{ display: 'block' }}>
          <rect x={0} y={0} width={padL} height={VH} fill="#1A1A1A" />
          {yTicks.map((v, i) => {
            const gy = (zeroY - (v / range) * plotH).toFixed(1)
            return (
              <text key={i} x={padL - 5} y={Number(gy) + 4}
                textAnchor="end" fill="#636366" fontSize={10} fontFamily="Inter, sans-serif"
              >
                {fmtY(v)}
              </text>
            )
          })}
        </svg>
      </div>

      {/* Scrollable plot */}
      <div
        ref={scrollRef}
        style={{
          flex: 1, minWidth: 0,
          overflowX: 'auto', overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}
      >
        <svg width={VW} height={VH} style={{ width: VW, height: VH, display: 'block' }}>

          {/* Horizontal grid lines */}
          {yTicks.map((v, i) => {
            const gy = (zeroY - (v / range) * plotH).toFixed(1)
            return (
              <line key={i}
                x1={0} y1={gy} x2={VW - padR} y2={gy}
                stroke={v === 0 ? '#3A3A3A' : 'rgba(255,255,255,0.05)'}
                strokeWidth={v === 0 ? 1.5 : 1}
                strokeDasharray={v === 0 ? undefined : '3 5'}
              />
            )
          })}

          {/* X-axis month labels */}
          {monthLabels.map(({ x, text }) => (
            <text key={x}
              x={x.toFixed(1)}
              y={VH - padB + 16}
              textAnchor="middle" fill="#8E8E93" fontSize={11} fontFamily="Inter, sans-serif"
            >
              {text}
            </text>
          ))}

          {/* Bars — только реальные свечи: вплотную внутри периода, зазор между периодами */}
          {layout.map(({ d, x }) => {
            const bx       = x
            const isActive = activeFullDate === d.fullDate
            const { y: bY, h: bH } = barRect(d.value)
            const isPos = d.value >= 0

            return (
              <rect key={d.fullDate ?? bx}
                x={bx.toFixed(1)} y={bY.toFixed(1)}
                width={barW} height={bH.toFixed(1)}
                rx={rx.toFixed(1)}
                fill={isPos
                  ? (isActive ? '#34D399' : '#10B981')
                  : (isActive ? '#FF6060' : '#EF4444')}
                opacity={isActive ? 1 : 0.85}
                style={{ cursor: 'pointer' }}
                onMouseEnter={e => onSetActive({ point: makePoint(d), ...getAnchor(e) })}
                onMouseLeave={() => onSetActive(null)}
                onClick={e => {
                  e.stopPropagation()
                  const anchor = getAnchor(e)   // compute NOW — before async setState
                  const pt     = makePoint(d)
                  onSetActive(prev =>
                    prev?.point?.fullDate === d.fullDate ? null : { point: pt, ...anchor }
                  )
                }}
              />
            )
          })}
        </svg>
      </div>
    </div>
  )
}

// ── Equity Curve — Всего ───────────────────────────────────────────────────
// Ось Y — фиксированная колонка слева, график — рядом (не перекрываются).
// Вертикальная шкала всегда содержит ноль, отрицательная область видна целиком.
function EquityCurve({ data, containerRef, scrollRef, cardW, t, activeFullDate, onSetActive }) {
  if (!data || data.length < 2) {
    return (
      <div style={{
        height: 160,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontSize: 13, color: '#3A3A3A' }}>—</span>
      </div>
    )
  }

  const VH       = 160
  const padL     = 40, padR = 12, padT = 16, padB = 28
  const plotH    = VH - padT - padB

  // Растягиваем кривую на всю ширину: точки распределяются до правого края
  // (с небольшим отступом padR) — кривая не сжата и не обрезана.
  const availW    = Math.max(80, (cardW || 320) - 16 - padL)
  const ptSpacing = (availW - padR) / Math.max(1, data.length - 1)
  const VW       = availW

  // Y-диапазон захватывает и отрицательную область. Если все значения
  // положительные — шкала от 0 до max; все отрицательные — от min до 0.
  const vals  = data.map(d => d.value)
  const maxV  = Math.max(0, ...vals)
  const minV  = Math.min(0, ...vals)
  let vTop    = maxV > 0 ? Math.ceil(maxV * 1.12 / 10) * 10 : 0
  let vBot    = minV < 0 ? Math.floor(minV * 1.12 / 10) * 10 : 0
  if (vTop - vBot === 0) { vTop = 10; vBot = 0 }   // эквити плоское — защита от деления на 0
  const range = vTop - vBot

  // Тики шкалы ОБЯЗАТЕЛЬНО включают ноль (раньше при отрицательном диапазоне
  // 0 не попадал в равномерные 5 точек).
  const yTicks = vBot < 0
    ? (vTop > 0
        ? [vBot, vBot / 2, 0, vTop / 2, vTop]
        : [vBot, vBot * 0.75, vBot * 0.5, vBot * 0.25, 0])
    : [0, vTop * 0.25, vTop * 0.5, vTop * 0.75, vTop]

  const toX = i => i * ptSpacing
  const toY = v => padT + plotH - ((v - vBot) / range) * plotH

  const xLabels = getMonthLabels(data, t.monthNames)

  // Smooth bezier path
  const pts = data.map((d, i) => ({ x: toX(i), y: toY(d.value) }))
  let linePath = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`
  for (let i = 1; i < pts.length; i++) {
    const cpx = ((pts[i - 1].x + pts[i].x) / 2).toFixed(1)
    linePath += ` C ${cpx} ${pts[i - 1].y.toFixed(1)}, ${cpx} ${pts[i].y.toFixed(1)}, ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)}`
  }
  const baseY    = (padT + plotH).toFixed(1)
  const fillPath = `${linePath} L ${pts[pts.length - 1].x.toFixed(1)} ${baseY} L ${pts[0].x.toFixed(1)} ${baseY} Z`

  const getAnchor = e => {
    const con  = containerRef.current?.getBoundingClientRect()
    const rect = e?.currentTarget?.getBoundingClientRect()
    if (!con || !rect) return { x: 80, y: 20, containerWidth: 320 }
    return {
      x:              rect.left + rect.width / 2 - con.left,
      y:              rect.top  - con.top,
      containerWidth: con.width,
    }
  }

  return (
    <div style={{ display: 'flex' }}>
      {/* Fixed Y-axis column */}
      <div style={{ width: padL, flexShrink: 0 }}>
        <svg width={padL} height={VH} style={{ display: 'block' }}>
          <rect x={0} y={0} width={padL} height={VH} fill="#1A1A1A" />
          {yTicks.map((v, i) => (
            <text key={i} x={padL - 5} y={toY(v) + 4}
              textAnchor="end" fill="#636366" fontSize={10} fontFamily="Inter, sans-serif"
            >
              {v.toFixed(0)}
            </text>
          ))}
        </svg>
      </div>

      {/* Scrollable plot */}
      <div
        ref={scrollRef}
        style={{
          flex: 1, minWidth: 0,
          overflowX: 'auto', overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}
      >
        <svg width={VW} height={VH} style={{ width: VW, height: VH, display: 'block' }}>
          <defs>
            <linearGradient id="eqFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#34D399" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#34D399" stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="eqLine" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor="#34D399" />
              <stop offset="100%" stopColor="#10B981" />
            </linearGradient>
          </defs>

          {/* Y grid (ноль выделен) */}
          {yTicks.map((v, i) => (
            <line key={i}
              x1={0} y1={toY(v).toFixed(1)} x2={VW - padR} y2={toY(v).toFixed(1)}
              stroke={v === 0 ? '#2A2A2A' : 'rgba(255,255,255,0.05)'}
              strokeWidth={1} strokeDasharray={v === 0 ? undefined : '3 5'}
            />
          ))}

          {/* X month labels */}
          {xLabels.map(({ i, text }) => (
            <text key={i}
              x={(i === 0 ? toX(i) + 4 : toX(i)).toFixed(1)}
              y={VH - padB + 16}
              textAnchor={i === 0 ? 'start' : 'middle'}
              fill="#8E8E93" fontSize={11} fontFamily="Inter, sans-serif"
            >
              {text}
            </text>
          ))}

          {/* Gradient fill + line */}
          <path d={fillPath} fill="url(#eqFill)" />
          <path d={linePath} fill="none" stroke="url(#eqLine)" strokeWidth={2} strokeLinecap="round" />

          {/* Dots — skip synthetic origin */}
          {data.map((d, i) => {
            if (d.origin) return null
            const px = toX(i), py = toY(d.value)
            const isActive = activeFullDate === d.fullDate
            return (
              <circle key={d.fullDate}
                cx={px.toFixed(1)} cy={py.toFixed(1)}
                r={isActive ? 4.5 : 2.5}
                fill={isActive ? '#34D399' : '#1A1A1A'}
                stroke="#34D399" strokeWidth={2}
                style={{ cursor: 'pointer' }}
                onMouseEnter={e => onSetActive({ point: d, ...getAnchor(e) })}
                onMouseLeave={() => onSetActive(null)}
                onClick={e => {
                  e.stopPropagation()
                  const anchor = getAnchor(e)
                  onSetActive(prev =>
                    prev?.point?.fullDate === d.fullDate ? null : { point: d, ...anchor }
                  )
                }}
              />
            )
          })}
        </svg>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────
export default function WeeklyChart() {
  const period = useDashboardStore(s => s.period)
  const t      = useT()
  const containerRef = useRef(null)
  const scrollRef    = useRef(null)
  const [active, setActive] = useState(null)
  const [cardW, setCardW]   = useState(0)
  const { chartData, weeklyChartData, monthlyChartData, dailyEquity } = useChartData()
  const BAR_CFG = {
    daily:   { data: chartData },
    weekly:  { data: weeklyChartData.filter(d => !d.future) },
    monthly: { data: monthlyChartData.filter(d => !d.future) },
  }

  // Измеряем ширину карточки, чтобы графики подгонялись под экран целиком
  // (ни левый, ни правый край не обрезаются).
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => setCardW(el.clientWidth)
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  // Clear tooltip when period changes
  useEffect(() => { setActive(null) }, [period])

  // График подогнан под ширину экрана, скролл не нужен. Сброс на всякий случай.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollLeft = 0
  }, [period])

  // Convert vertical wheel to horizontal scroll when cursor is over the chart
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handler = e => {
      if (el.scrollWidth <= el.clientWidth) return
      e.preventDefault()
      el.scrollLeft += e.deltaY
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [period])

  const isTotal = period === 'total'
  const barCfg  = isTotal ? null : (BAR_CFG[period] ?? BAR_CFG.daily)

  // Пустой массив = сделок за период нет. Показываем честный empty-state,
  // а не демо-данные. Для бара-графика «есть данные» = есть хотя бы одна
  // РЕАЛЬНАЯ свеча (trades > 0); только пустые периоды = «Нет данных».
  const renderData = isTotal ? dailyEquity : (barCfg?.data ?? [])
  const hasData    = Array.isArray(renderData) && (isTotal
    ? renderData.length > 0
    : renderData.some(d => d.trades > 0))

  return (
    <div
      ref={containerRef}
      className="rounded-2xl mb-4"
      style={{
        background: '#1A1A1A',
        paddingTop: 16, paddingBottom: 8, paddingLeft: 4, paddingRight: 12,
        position: 'relative',
      }}
      onMouseLeave={() => setActive(null)}
      onClick={() => setActive(null)}
    >
      {/* Tooltip */}
      {active && (
        <ChartTooltip
          point={active.point}
          x={active.x}
          y={active.y}
          containerWidth={active.containerWidth}
          t={t}
          detailed={!isTotal}
        />
      )}

      {!hasData ? (
        <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 13, color: '#636366' }}>{t.chartNoData}</span>
        </div>
      ) : isTotal ? (
        // ── Всего: daily equity curve (cumulative, scrollable) ───────────────
        <EquityCurve
          data={dailyEquity}
          containerRef={containerRef}
          scrollRef={scrollRef}
          cardW={cardW}
          t={t}
          activeFullDate={active?.point?.fullDate}
          onSetActive={setActive}
        />
      ) : (
        // ── День / Неделя / Месяц: bar chart ─────────────────────────────
        <BarChart
          data={barCfg.data}
          containerRef={containerRef}
          scrollRef={scrollRef}
          cardW={cardW}
          t={t}
          activeFullDate={active?.point?.fullDate}
          onSetActive={setActive}
        />
      )}
    </div>
  )
}
